import { apiCall, apiUpload } from './apiClient';

export const extractFabricImageCandidates = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.flatMap((entry) => extractFabricImageCandidates(entry));
  }

  if (typeof value === 'object') {
    return extractFabricImageCandidates(
      value.image_url ||
        value.imageUrl ||
        value.url ||
        value.location ||
        value.path ||
        value.key ||
        value.s3Url ||
        value.s3_url ||
        value.media_url ||
        value.file_url ||
        value.images ||
        value.image
    );
  }

  const stringValue = String(value).trim();
  if (!stringValue) return [];

  const markdownLinkMatch = stringValue.match(/\[([^\]]+)\]\(([^)]+)\)/);
  if (markdownLinkMatch) {
    return [markdownLinkMatch[2], markdownLinkMatch[1]];
  }

  const markdownUrlMatch = stringValue.match(/\((https?:\/\/[^)]+)\)/);
  if (markdownUrlMatch?.[1]) {
    return [markdownUrlMatch[1]];
  }

  const bareUrls = stringValue.match(/https?:\/\/[^\s"'<>]+/g);
  if (bareUrls?.length) {
    return bareUrls.map((url) => url.replace(/[)\],.]+$/, ''));
  }

  return stringValue
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

export const normalizeFabricRecord = (record) => {
  const item = record?.data ?? record?.fabric ?? record;
  if (Array.isArray(item)) {
    return normalizeFabricRecord(item[0]);
  }

  if (!item || typeof item !== 'object') {
    return item;
  }

  const imageItemsSource =
    item.fabric_images ||
    item.fabricImages ||
    item.fabric_image ||
    item.fabricImage ||
    item.imageItems ||
    item.image_items ||
    item.images ||
    item.imageUrls ||
    item.image_urls ||
    item.image ||
    [];

  const imageEntries = Array.isArray(imageItemsSource) ? imageItemsSource : [imageItemsSource];

  const imageItems = imageEntries
    .flatMap((entry, index) => {
      if (!entry) return [];

      if (typeof entry === 'object') {
        const rawUrl =
          entry.image_url ||
          entry.imageUrl ||
          entry.url ||
          entry.location ||
          entry.path ||
          entry.key ||
          entry.s3Url ||
          entry.s3_url ||
          entry.media_url ||
          entry.file_url ||
          entry.image ||
          '';

        const candidates = extractFabricImageCandidates(rawUrl);
        const url = candidates[0] || '';

        return [{
          id: entry.fabric_image_id || entry.fabricImageId || entry.id || entry.image_id || entry.imageId || undefined,
          url,
          displayOrder: Number(entry.displayOrder ?? entry.display_order ?? entry.displayorder ?? index + 1) || index + 1,
        }];
      }

      const candidates = extractFabricImageCandidates(entry);
      return candidates.map((candidate, candidateIndex) => ({
        url: candidate,
        displayOrder: index + candidateIndex + 1,
      }));
    })
    .filter((image) => image?.url);

  imageItems.sort((a, b) => {
    const orderDiff = (b.displayOrder || 0) - (a.displayOrder || 0);
    if (orderDiff !== 0) return orderDiff;

    const idA = Number(a.id || 0);
    const idB = Number(b.id || 0);
    return idB - idA;
  });

  const imageUrls = [...new Set(imageItems.map((item) => item.url).filter(Boolean))];

  return {
    ...item,
    ID: item.fabric_id || item.id || item.record_group_id,
    uniqueRecordId: item.record_group_id,
    imageItems,
    imageUrls,
    fabrictype: item.fabrictype || item.fabricType || item.fabric_type,
  };
};

export const postFabricData = (data) =>
  apiCall(`/designers/fabrics`, {
    method: 'POST',
    body: JSON.stringify(data)
  });

export const getFabricData = (fabricId) => {
  const normalizedFabricId = String(fabricId ?? '').trim();
  const endpoint = normalizedFabricId
    ? `/designers/fabrics/${encodeURIComponent(normalizedFabricId)}`
    : `/designers/fabrics`;

  return apiCall(endpoint, {
    method: 'GET'
  });
};

export const postImageFabric = (formData) =>
  apiUpload(`/designers/fabrics/upload`, formData, {
    method: 'POST'
  });

export const putEditFabric = (fabricId, data) => {
  const normalizedFabricId = String(fabricId ?? '').trim();

  if (!normalizedFabricId) {
    throw new Error('Fabric id is missing.');
  }

  return apiCall(`/designers/fabrics/${encodeURIComponent(normalizedFabricId)}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

export const deleteFabricData = (fabricId) => {
  const normalizedFabricId = String(fabricId ?? '').trim();

  if (!normalizedFabricId) {
    throw new Error('Fabric id is missing.');
  }

  return apiCall(`/designers/fabrics/${encodeURIComponent(normalizedFabricId)}`, {
    method: 'DELETE'
  });
};

export const updateFabricImage = (imageId, data) => {
  const normalizedImageId = String(imageId ?? '').trim();

  if (!normalizedImageId) {
    throw new Error('Fabric image id is missing.');
  }

  return apiCall(`/designers/fabrics/images/${encodeURIComponent(normalizedImageId)}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

export const deleteFabricImage = (imageId) => {
  const normalizedImageId = String(imageId ?? '').trim();

  if (!normalizedImageId) {
    throw new Error('Fabric image id is missing.');
  }

  return apiCall(`/designers/fabrics/images/${encodeURIComponent(normalizedImageId)}`, {
    method: 'DELETE'
  });
};
